import { LegalProjections, RegionBounds } from "@agencebio/cartobio-types";
import intersect from "@turf/intersect";
import bboxPolygon from "@turf/bbox-polygon";
import { reproject } from "reproject";
import proj4 from "proj4";
import { polygonArea } from "geometric";
import union from "@turf/union";
import axios from "axios";
import { feature, featureCollection } from "@turf/helpers";
import { parseReference } from "@/utils/cadastre.js";
import bbox from "@turf/bbox";
import { fromCodeCpf } from "@agencebio/rosetta-cultures";
import difference from "@turf/difference";
import { conversionLevels } from "@/referentiels/ab.js";
import { toDateInputString } from "@/utils/dates.js";

/**
 * @typedef {import('geojson').Feature} Feature
 * @typedef {import('geojson').Geometry} Geometry
 * @typedef {import('geojson').FeatureCollection} FeatureCollection
 */

/**
 * @typedef IgnFeature
 * @property {String=} id
 * @property {Geometry} geometry
 * @property {IgnFeatureProperties} properties
 * @property {String} type
 */
/**
 * @typedef IgnFeatureProperties
 * @property {String} id
 * @property {String} departmentcode
 * @property {String} municipalitycode
 * @property {String} oldmunicipalitycode
 * @property {String} districtcode
 * @property {String} section
 * @property {String} sheet
 * @property {String} number
 * @property {String} city
 * @property {Geometry} truegeometry
 */

/**
 * @typedef GroupingChoice
 * @property {String} label
 * @property {function(Feature): String|Number} datapoint
 * @property {function(Feature): String} groupLabelFn
 * @property {function(FeatureGroup, FeatureGroup): Number} sortFn
 */

/**
 * @typedef FeatureGroup
 * @property {String} label
 * @property {String} key the datapoint in common with grouped items
 * @property {String} mainKey in case of multiple pivots, this ; otherwise it has the same value as key
 * @property {String} pivot grouping strategy identifier
 * @property {Feature[]} features
 * @property {Number} surface
 */

/**
 * @typedef DeletionReason
 * @property {DeletionReasonsCode} code
 * @property {String} label
 */

/**
 * @param {CartoBioFeature[]|CartoBioFeature|CartoBioFeatureCollection} feature ou collection - en WGS84
 * @return {Number} surface en mètres carrés
 */
export function legalProjectionSurface(feature) {
  if (feature.type === "FeatureCollection") {
    feature = feature.features;
  }
  if (!Array.isArray(feature) && !feature.geometry) {
    return NaN;
  }

  if (Array.isArray(feature)) {
    return feature.reduce((total, f) => total + legalProjectionSurface(f), 0);
  }

  const area = (Object.entries(RegionBounds).find(([, bounds]) => {
    return intersect(featureCollection([feature, bboxPolygon(bounds)]));
  }) || ["metropole"])[0];

  // Si on a un cas avec des multipolygones, on prends le premier pour le calcul
  if (Array.isArray(feature.geometry.coordinates) && feature.geometry.coordinates.length > 0) {
    if (
      Array.isArray(feature.geometry.coordinates[0]) &&
      Array.isArray(feature.geometry.coordinates[0][0]) &&
      Array.isArray(feature.geometry.coordinates[0][0][0])
    ) {
      return "NC";
    }
  }

  const projection = LegalProjections[area];
  const coordinates = reproject(feature, proj4.WGS84, projection).geometry.coordinates;
  const outer = coordinates[0];
  const inner = coordinates.slice(1);

  // les coordonnées nouvellement projetées sont en mètres carrés,
  // on peut donc utiliser un simple calcul géométrique d'aire
  return inner.reduce((total, hole) => {
    return total - polygonArea(hole);
  }, polygonArea(outer));
}

/**
 * @typedef FeaturePropertyCulture
 * @property {String} CPF
 * @property {=String} TYPE
 * @property {=String} variete
 * @property {Number} surface
 */

export const GROUPE_NONE = "";
export const GROUPE_COMMUNE = "COMMUNE";
export const GROUPE_CULTURE = "CULTURE";
export const GROUPE_ILOT = "ILOT";
export const GROUPE_NIVEAU_CONVERSION = "NIVEAU_CONVERSION";
export const GROUPE_DATE_ENGAGEMENT = "DATE_ENGAGEMENT";
export const GROUPE_ANNEE_ENGAGEMENT = "ANNEE_ENGAGEMENT";
/**  @enum {String} */
export const DeletionReasonsCode = {
  FEATURE_COLLECTION_LIFECYCLE: "lifecycle",
  OTHER: "other",
  USER_ERROR: "error",
};
/**
 * @type {DeletionReason[]}
 */
export const deletionReasons = [
  { code: DeletionReasonsCode.USER_ERROR, label: "Erreur" },
  { code: DeletionReasonsCode.FEATURE_COLLECTION_LIFECYCLE, label: "Évolution du parcellaire" },
  { code: DeletionReasonsCode.OTHER, label: "Autre" },
];

class FeatureError extends ReferenceError {
  name = "FeatureError";
  #label = "Erreur de parcelle";

  constructor(featureId) {
    super(`Problème avec la parcelle ${featureId}`);

    this.id = featureId;
  }

  /**
   * As displayed in the UI
   *
   * @returns {String}
   */
  toString() {
    return this.message;
  }

  /**
   * As POSTed via the HTTP API
   */
  valueOf() {
    return { id: this.id, name: this.#label };
  }
}

export class FeatureNotFoundError extends FeatureError {
  name = "FeatureNotFoundError";
  label = "Parcelle introuvable";

  toString() {
    return `La référence cadastrale ${this.id} est introuvable dans le parcellaire informatisé.`;
  }
}

export class FeatureWithoutGeometryError extends FeatureError {
  name = "FeatureWithoutGeometryError";
  label = "Contour géographique absent";

  toString() {
    return `Impossible d'obtenir le dessin géographique de la référence cadastrale ${this.id} n'a pas été retournée par le parcellaire informatisé.`;
  }
}

/**
 * @enum {Number}
 */
export const SORT = {
  ASCENDING: 1,
  DESCENDING: -1,
};

/**
 *
 * @param {function(Feature): function} propertyAccessor
 * @param {SORT} order
 * @returns {function(GroupOrFeature, GroupOrFeature): Number}
 */
export function sortByAccessor(propertyAccessor, order = SORT.ASCENDING) {
  const isNumeric = (d) => typeof d === "number" || Number.isNaN(parseInt(d, 10)) === false;

  return function sortByAscendingFeatureGroupProperty(groupA, groupB) {
    const a = String(propertyAccessor(groupA));
    const b = String(propertyAccessor(groupB));

    return (
      a.localeCompare(b, "fr-FR", {
        usage: "sort",
        numeric: isNumeric(propertyAccessor(groupA)),
      }) * order
    );
  };
}

export const NO_GROUP = "__nogroup__";
export const PACNotationOptions = {
  ilotLabel: "",
  parcelleLabel: "",
  separator: ".",
};
/**
 * @type {Record<string, GroupingChoice>}
 */
export const groupingChoices = {
  [GROUPE_COMMUNE]: {
    label: "Commune",
    labelNoGroup: "Commune inconnue",
    labelEtranger: "Parcelles à l'étranger",
    datapoint: (d) => d.properties.COMMUNE || NO_GROUP,
    groupLabelFn({ featureSample: d }) {
      if (d.properties.COMMUNE_LABEL) {
        return `${d.properties.COMMUNE_LABEL} (${d.properties.COMMUNE.slice(0, -3)})`;
      } else if (d.properties.COMMUNE) {
        return d.properties.COMMUNE;
      } else if (d.properties.etranger) {
        return this.labelEtranger;
      } else {
        return this.labelNoGroup;
      }
    },
    sortFn: sortByAccessor((g) => g.surface, SORT.DESCENDING),
    sortFeaturesFn: sortByAccessor((f) => featureName(f, PACNotationOptions), SORT.ASCENDING),
  },
  [GROUPE_ILOT]: {
    label: "Îlot PAC ou hors PAC",
    labelNoGroup: "Non précisé ou hors-PAC",
    labelEnAttente: "En attente déclaration PAC",
    /** @param {GeoJSONFeature} */
    datapoint: (d) => {
      if (d.properties.NUMERO_I && d.properties.NUMERO_I !== "0") return d.properties.NUMERO_I;
      return d.properties.attente_pac ? "attente_pac" : NO_GROUP;
    },
    groupLabelFn({ featureSample: d, groupingKey }) {
      if (d.properties.NUMERO_I && d.properties.NUMERO_I !== "0") {
        return `Îlot ${d.properties.NUMERO_I}`;
      } else if (groupingKey === "attente_pac") {
        return this.labelEnAttente;
      } else {
        return this.labelNoGroup;
      }
    },
    sortFn: sortByAccessor((g) => {
      const key = g.features.at(0)?.properties?.NUMERO_I;
      if (!key || key === "0") {
        const groupingKey = g.key;
        if (groupingKey === "attente_pac") return Number.MAX_SAFE_INTEGER - 1;
        return Number.MAX_SAFE_INTEGER;
      }
      return parseInt(key, 10);
    }, SORT.ASCENDING),
    sortFeaturesFn: sortByAccessor((f) => parseInt(f.properties?.NUMERO_P, 10) || Infinity, SORT.ASCENDING),
  },
  [GROUPE_CULTURE]: {
    label: "Culture",
    labelNoGroup: "Absence de culture",
    labelUnknown: "Culture inconnue",
    /** @param {GeoJSONFeature} */
    datapoint: (d) =>
      Array.isArray(d.properties.cultures) && d.properties.cultures.length
        ? d.properties.cultures.map(({ CPF }) => CPF)
        : [NO_GROUP],
    groupLabelFn({ datapoint }) {
      return datapoint === NO_GROUP ? this.labelNoGroup : fromCodeCpf(datapoint)?.libelle_code_cpf || this.labelUnknown;
    },
    sortFn: sortByAccessor((g) => fromCodeCpf(g.mainKey)?.libelle_code_cpf || "", SORT.ASCENDING),
    sortFeaturesFn: sortByAccessor((f) => featureName(f, PACNotationOptions), SORT.ASCENDING),
  },
  [GROUPE_NIVEAU_CONVERSION]: {
    label: "Niveau de conversion",
    labelNoGroup: "Niveau de conversion inconnu",
    /** @param {GeoJSONFeature} */
    datapoint: (d) => d.properties.conversion_niveau || NO_GROUP,
    groupLabelFn({ groupingKey }) {
      return conversionLevels.find(({ value }) => value === groupingKey)?.label || this.labelNoGroup;
    },
    sortFn: sortByAccessor((g) => g.surface, SORT.DESCENDING),
    sortFeaturesFn: sortByAccessor((f) => featureName(f, PACNotationOptions), SORT.ASCENDING),
  },
  [GROUPE_ANNEE_ENGAGEMENT]: {
    label: "Année de conversion",
    labelNoGroup: "Absence de date de début de conversion",
    /** @param {GeoJSONFeature} */
    datapoint: (d) => (d.properties.engagement_date ? new Date(d.properties.engagement_date).getFullYear() : NO_GROUP),
    groupLabelFn({ groupingKey }) {
      return groupingKey === NO_GROUP ? this.labelNoGroup : groupingKey;
    },
    sortFn: sortByAccessor((g) => g.features?.at(0)?.properties?.engagement_date ?? 0, SORT.DESCENDING),
    sortFeaturesFn: sortByAccessor((f) => featureName(f, PACNotationOptions), SORT.ASCENDING),
  },
};

Object.defineProperty(groupingChoices, GROUPE_DATE_ENGAGEMENT, {
  enumerable: false,
  value: {
    label: null,
    /** @param {GeoJSONFeature} */
    datapoint: (d) => (d.properties.engagement_date ? toDateInputString(new Date(d.properties.engagement_date)) : ""),
    groupLabelFn: ({ groupingKey }) => groupingKey || "Année de début de conversion inconnue",
    sortFn: sortByAccessor((g) => g.key, SORT.DESCENDING),
    sortFeaturesFn: sortByAccessor((f) => featureName(f, PACNotationOptions), SORT.ASCENDING),
  },
});

export function createGroupingKeys(elements) {
  const keys = elements.reduce((keys, item, i) => {
    if (Array.isArray(item)) {
      keys = item.map((subkey) => [...keys, subkey]);
    } else {
      // we init for the first item
      if (i === 0) {
        keys.push([]);
      }

      keys.forEach((k) => k.push(item));
    }

    return keys;
  }, []);

  return Array.from(new Set(keys.map((key) => key.join("-"))));
}

/**
 *
 * @param {FeatureCollection} collection
 * @param {String} pivot
 * @returns {FeatureGroup[]}
 */
export function getFeatureGroups(collection, pivot = GROUPE_CULTURE, input) {
  // Use a default pivot if none is provided
  if (pivot === "" || (Array.isArray(pivot) && pivot.length === 0)) {
    return [
      {
        label: "",
        key: "none",
        pivot,
        features: collection.features,
        surface: inHa(legalProjectionSurface(featureCollection(collection.features))),
      },
    ];
  }

  const pivots = Array.isArray(pivot) ? pivot : [pivot];

  /**
   * If [CULTURE, NIVEAU_CONVERSION, ANNEE_CONVERSION]
   * With [ [1a, [01.92, 01.26.1], AB, 2019], [1b, [01.92], AB, 2010] ]
   * Should expand to [ ['01.92-AB-2019', ...], ['01.26.1-AB-2019', ...], ['01.92-AB-2010', ...] ]
   * With step 1 [ [01.92, AB, 2019], [01.26.1, AB, 2019], [01.92, AB, 2010] ]
   */
  const groups = collection.features.reduce((groups, feature) => {
    const datapoints = pivots.map((pivot) => groupingChoices[pivot].datapoint(feature));
    const groupingKeys = createGroupingKeys(datapoints);

    groupingKeys.forEach((groupKey) => {
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.set(groupKey, [...groups.get(groupKey), feature].sort(groupingChoices[pivots.at(0)].sortFeaturesFn));
    });

    return groups;
  }, new Map());

  return Array.from(groups)
    .map(([groupingKey, features]) => ({
      label: groupingChoices[pivots.at(0)].groupLabelFn({
        featureSample: features[0],
        groupingKey,
        datapoint: groupingKey.split("-").at(0),
      }),
      key: groupingKey,
      mainKey: groupingKey.split("-").at(0),
      pivot: pivots.at(0),
      features: features.filter((feature) => {
        if (input && feature.properties) {
          const inputLower = input
            .normalize("NFD")
            .replace(/\p{Diacritic}/gu, "")
            .toLowerCase();
          const matches = input.match(/\d+/g);
          const matchIlot = inputLower.includes("ilot") ? matches?.[0] : null;
          const matchParcelle = inputLower.includes("parcelle")
            ? matches?.length > 1
              ? matches[1]
              : matches?.[0]
            : null;

          const searchAllIlot = "ilot".includes(inputLower.trim());
          const searchAllParcelle = "parcelle".includes(inputLower.trim());

          return (
            (feature.properties.NOM &&
              feature.properties.NOM.normalize("NFD")
                .replace(/\p{Diacritic}/gu, "")
                .toLowerCase()
                .includes(inputLower)) ||
            (feature.properties.NUMERO_I && feature.properties.NUMERO_I == input) ||
            (feature.properties.NUMERO_P && feature.properties.NUMERO_P == input) ||
            (matchIlot && matchParcelle
              ? feature.properties.NUMERO_I == matchIlot && feature.properties.NUMERO_P == matchParcelle
              : (matchIlot && feature.properties.NUMERO_I == matchIlot) ||
                (matchParcelle && feature.properties.NUMERO_P == matchParcelle)) ||
            (searchAllIlot && feature.properties.NUMERO_I) ||
            (searchAllParcelle && feature.properties.NUMERO_P)
          );
        }
        return true;
      }),
      surface: legalProjectionSurface(features),
    }))
    .sort(groupingChoices[pivots.at(0)].sortFn)
    .filter((e) => e.features.length > 0);
}

/**
 * @param {Feature[]} features
 * @param {String} id
 * @returns {Feature}
 */
export function getFeatureById(features, id) {
  return features.find((feature) => feature.id === id);
}

/**
 * @param {Feature} feature
 * @param {{ explicitName?: boolean, ilotLabel?: string, parcelleLabel?: string, separator?: string, placeholder?: string}} options
 * @returns {String}
 */
export function featureName(
  feature,
  {
    explicitName = true,
    ilotLabel = "îlot ",
    parcelleLabel = "parcelle ",
    separator = ", ",
    placeholder = "-",
    hint = false,
    nameOnly = false,
  } = {},
) {
  const NUMERO_I = parseInt(feature.properties.NUMERO_I, 10);
  const NUMERO_P = parseInt(feature.properties.NUMERO_P, 10);

  if (nameOnly) {
    return feature.properties.NOM ?? null;
  }

  if (hint) {
    const ilot = Number.isNaN(NUMERO_I) ? "" : NUMERO_I;
    const parcelle = Number.isNaN(NUMERO_P) ? "" : NUMERO_P;
    const base = ilot && parcelle ? `${ilot}.${parcelle}` : ilot || parcelle || "";
    if (base) {
      return feature.properties.NOM ? `${base} (${feature.properties.NOM})` : base;
    }
    return feature.properties.NOM ?? placeholder;
  }

  if (feature.properties.NOM || !Number.isNaN(NUMERO_I) || !Number.isNaN(NUMERO_P)) {
    const name = [
      Number.isNaN(NUMERO_I) === false ? `${ilotLabel}${NUMERO_I}` : "",
      Number.isNaN(NUMERO_P) === false ? `${parcelleLabel}${NUMERO_P}` : "",
    ]
      .filter((d) => d)
      .join(separator);

    if (!name) {
      return feature.properties.NOM ?? placeholder;
    }

    return `${name ?? placeholder}${explicitName && feature.properties.NOM ? ` (${feature.properties.NOM})` : ""}`;
  }

  if (feature.properties.cadastre) {
    if (Array.isArray(feature.properties.cadastre)) {
      const references = feature.properties.cadastre.map((cadastre) => parseReference(cadastre));
      return `Parcelles ${references.map((cadastre) => cadastre.number).join(separator)}`;
    }

    const { prefix, section, number } = parseReference(feature.properties.cadastre);
    return ["Reférence cadastrale", prefix !== "000" ? prefix : "", section, number].filter((d) => d).join(" ");
  } else {
    return placeholder;
  }
}

export function featureDetails(feature) {
  const details = [];

  if (feature.properties.cadastre) {
    const cadastre = Array.isArray(feature.properties.cadastre)
      ? feature.properties.cadastre
      : [feature.properties.cadastre];

    cadastre
      .map((ref) => parseReference(ref))
      .forEach(({ section, number }) => {
        details.push(`Parcelle cadastrale ${number} (Section ${section}, ${feature.properties.COMMUNE_LABEL})`);
      });
  }

  return details;
}

const cultureList = new Intl.ListFormat("fr", {
  style: "short",
  type: "conjunction", // "et"
});

/**
 *
 * @param {FeaturePropertyCulture} culture
 * @param {{ withCode: boolean }} options
 * @returns {string}
 */
export function cultureLabel(culture, { withCode = false } = {}) {
  if (!culture) {
    return groupingChoices[GROUPE_CULTURE].labelNoGroup;
  }

  const label = fromCodeCpf(culture.CPF)?.libelle_code_cpf || groupingChoices[GROUPE_CULTURE].labelUnknown;

  return withCode ? `${culture.CPF ?? culture.TYPE} ${label}` : label;
}

/**
 *
 * @param {FeaturePropertyCulture[]} cultures
 * @returns {string}
 */
export function cultureLabels(cultures, options) {
  const labels = new Set(cultures.map((culture) => cultureLabel(culture, options)));

  return cultureList.format(labels);
}

/**
 * @param {String|Number} value
 * @returns {String}
 */
export function inHa(value) {
  if (!isNaN(value))
    return parseFloat(value / 10000).toLocaleString("fr-FR", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  return "NC";
}

export function bounds(featureCollection, defaults = bounds.DEFAULT_BOUNDS) {
  if (
    !featureCollection?.type ||
    (featureCollection?.type === "FeatureCollection" && !featureCollection.features.length)
  ) {
    return defaults;
  }

  // if the input is out of bounds (eg: absent geometry), bbox returns [Infinite, Infinite, -Infinite, -Infinite]
  // but mostly, MapLibre fails if the bounds are not withing the Web Mercator bounds
  const result = bbox(featureCollection);
  return Math.abs(result.at(0)) > 90 ? defaults : result;
}

bounds.DEFAULT_BOUNDS = [
  [-9.86, 41.15],
  [10.38, 51.56],
];

/**
 * Returns a geometry, without any content part of a feature collection
 *
 * @param {Feature} feature
 * @param {FeatureCollection} featureCollection
 * @returns {Feature}
 */
export function diff(feature, featureCollection) {
  return featureCollection.features.reduce((reducedFeature, target) => {
    if (reducedFeature === null) {
      return null;
    }

    if (!intersect(featureCollection([reducedFeature, target]))) {
      return reducedFeature;
    }

    return difference(reducedFeature, target);
  }, feature);
}

/**
 * Merge all features into one single feature (union)
 *
 * @param {Array<Feature>} features
 * @returns {Feature}
 */
export function merge(features) {
  return features.reduce((mergedFeature, feature) => {
    if (mergedFeature === null) {
      return feature;
    }

    return union(mergedFeature, feature);
  }, null);
}

/**
 * Fetch a single geometry for a given cadastral reference
 *
 * @param {String} q
 * @param {Feature} baseFeature
 * @throws {Error}
 * @returns {Promise<{ geometry: Geometry, feature: Feature }>}
 */
async function fetchCadastreGeometry(q, baseFeature) {
  const { data } = await axios.get("https://data.geopf.fr/geocodage/search", {
    params: {
      q,
      index: "parcel",
      limit: 1,
      returntruegeometry: true,
    },
  });

  /** @type {IgnFeature=} */
  const cadastreFeature = data.features.at(0);

  if (!cadastreFeature) {
    throw new FeatureNotFoundError(q);
  }

  const geometry = cadastreFeature.properties.truegeometry;

  if (!geometry) {
    throw new FeatureWithoutGeometryError(q);
  }
  return {
    geometry,
    feature: feature(
      geometry,
      {
        ...baseFeature.properties,
        COMMUNE_LABEL: cadastreFeature.properties.city,
        COMMUNE: `${cadastreFeature.properties.departmentcode}${cadastreFeature.properties.municipalitycode}`,
      },
      { id: baseFeature.id ?? baseFeature.properties.id },
    ),
  };
}

/**
 * Augments features with no geometry and
 * @param {FeatureCollection} baseCollection
 * @param {String} field feature property name to collect and fetch the geometry against
 * @throws {Error}
 * @returns {Promise<{ featureCollection: FeatureCollection, warnings: String[] }>}
 */
export async function applyCadastreGeometries(baseCollection, field = "cadastre") {
  const warnings = [];

  // L'api du récupération du cadastre a une limite de 50 appels/seconde
  const results = await Promise.allSettled(
    baseCollection.features.map((feature, index) => {
      if (!feature.geometry && Array.isArray(feature.properties[field])) {
        return new Promise((resolve) =>
          setTimeout(
            () => {
              resolve(fetchCadastreGeometry(feature.properties[field][0], feature));
            },
            1500 * (index / 50),
          ),
        );
      }
    }),
  );

  const featureCollection = {
    ...baseCollection,
    features: results
      .map(({ status, value, reason }) => {
        if (status === "rejected") {
          warnings.push(reason);
          return;
        }

        return value.feature;
      })
      .filter((feature) => feature),
  };

  return { featureCollection, warnings };
}

/**
 * @param {Feature} feature
 * @returns {String}
 */
export function getTimeAgo(feature) {
  if (feature.properties.updatedAt === feature.properties.createdAt) return "";

  const now = new Date();
  const date = new Date(feature.properties.updatedAt);
  const diffInMs = now - date;
  const diffInMinutes = Math.floor(diffInMs / 1000 / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInMinutes / 1440);
  const diffInMonths = Math.floor(diffInDays / 30);

  if (diffInMinutes < 1) {
    return "à l'instant";
  } else if (diffInMinutes < 60) {
    return `
    il y a ${diffInMinutes} min`;
  } else if (diffInHours < 24) {
    return `il y a ${diffInHours} h`;
  } else if (diffInDays < 30) {
    return `il y a ${diffInDays} jour${diffInDays > 1 ? "s" : ""}`;
  } else if (diffInMonths < 12) {
    return `il y a ${diffInMonths} mois`;
  } else {
    const formattedDate = date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return `Le ${formattedDate}`;
  }
}

/***
 * @param {String} key
 * @returns {String}
 */
export function getCultureIcon(key) {
  if (key === NO_GROUP) {
    return "";
  }

  const groupeCulture = fromCodeCpf(key)?.groupe;
  if (!groupeCulture) {
    return "";
  }

  const res = groupeCulture
    .toLocaleLowerCase()
    .replace(" ", "-")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (
    [
      "autres-surfaces",
      "fruits",
      "grandes-cultures",
      "legumes",
      "plantes-aromatiques",
      "viticulture",
      "surfaces-fourrageres",
    ].includes(res)
  ) {
    return (
      "fr-icon-culture-" +
      groupeCulture
        .toLocaleLowerCase()
        .replace(" ", "-")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
    );
  }

  return "fr-icon-culture-autres-surfaces";
}
